/**
 * Marks the docked search panel. The shortcut looks for it by attribute rather
 * than by ref because the panel renders in BoardTopBarV2 while the handler
 * lives in BoardCanvas — neither owns the other.
 */
export const BOARD_FILE_SEARCH_ATTR = 'data-board-file-search'

/**
 * Who owns Cmd+F / Ctrl+F on a board.
 *
 * Extracted from BoardCanvas so the rules are testable — rendering BoardCanvas
 * means rendering tldraw. The rules, in order:
 *
 *  - A card preview wins while it's open: its own find-in-file handles the key
 *    (FS-657). The preview modal renders *inside* the tldraw tree, so a plain
 *    "is the target on the canvas?" check matches it too, and the board's
 *    file-name search used to open on top of the preview.
 *  - The board search field itself wins, even though it is an input and now
 *    lives in the top bar rather than on the canvas: pressing the shortcut
 *    again should put the caret back in it, not hand the key to the browser.
 *  - Typing in any other input keeps the key (so it reaches that field's own
 *    search).
 *  - Anywhere outside the canvas (e.g. the chat panel) keeps the browser's
 *    native find-in-page.
 */
export function shouldOpenBoardFileSearch(args: {
  isPreviewOpen: boolean
  /** A keydown can be targeted at window/document, which `contains` rejects. */
  target: EventTarget | null
  canvas: HTMLElement | null
}): boolean {
  const { isPreviewOpen, canvas } = args
  if (isPreviewOpen) return false
  const target = args.target
  if (!(target instanceof HTMLElement)) return false

  // Checked before the input rule below — the search field IS an input.
  if (target.closest(`[${BOARD_FILE_SEARCH_ATTR}]`)) return true

  const isTextInput =
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    (target.isContentEditable && target.getAttribute('contenteditable') === 'true')
  if (isTextInput) return false

  return !!canvas?.contains(target)
}
